import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import api from '@/admin/lib/api'

/**
 * WPS-157 §13: tenant-level documentation-reminder cadence, kept separately for
 * claims and transfers. A `null` value means "not set" - the scheduled sweep
 * falls back to the tenant trigger schedule for that field.
 */
export interface ReminderCadence {
  interval_days: number | null
  max_reminders: number | null
}

/** Claims only: whether reminders go, and what happens when nothing arrives. */
export interface ClaimReminderSettings extends ReminderCadence {
  send_reminders: boolean
  close_after_days: number | null
  auto_close: boolean
  closure_email: boolean
  reopen_on_documents: boolean
  /** Read-only: months after a no-response close that documents still reopen it. */
  reopen_window_months: number
  /** Read-only: when reminders were turned on for new requests only. */
  reminders_start_at: string | null
}

/** Transfers: cadence plus what a new document request starts with (WPS-277). */
export interface TransferReminderSettings extends ReminderCadence {
  /** "Set up automatic reminder" starts ticked on a new request. */
  remind_by_default: boolean
  /** "Cancel if nothing arrives" starts on for a new request with a reminder. */
  auto_cancel: boolean
  cancel_after_days: number | null
  /** Email the customer when a transfer is cancelled because nothing arrived. */
  cancellation_email: boolean
}

/** Claim counts by reminder state. */
export interface ReminderSummary {
  waiting: number
  due_today: number
  closing_7d: number
  off: number
}

/** What claim reminders still need before one can go out. */
export interface ReminderSetup {
  has_document_request: boolean
  sends_document_requests: boolean
  transfers_available: boolean
  /** False when the reminder email's wording has lost the final "will close" warning. */
  reminder_has_final_warning: boolean
  /** False when the transfer reminder email has no final "will be cancelled" warning. */
  transfer_reminder_has_final_warning?: boolean
}

/** Transfer counts by reminder state (WPS-277). */
export interface TransferReminderSummary {
  waiting: number
  due_today: number
  cancelling_7d: number
}

/** The names of the emails reminders send; `null` for one not set up yet. */
export interface ReminderEmails {
  reminder: string | null
  closure: string | null
  transfer_reminder: string | null
}

export interface ReminderSettings {
  claims: ClaimReminderSettings
  transfers: TransferReminderSettings
  summary: ReminderSummary
  /** Optional: a payload cached before WPS-277 has none. */
  transfer_summary?: TransferReminderSummary
  setup: ReminderSetup
  emails: ReminderEmails
}

type ClaimReminderUpdate = Partial<
  Pick<
    ClaimReminderSettings,
    | 'send_reminders'
    | 'interval_days'
    | 'max_reminders'
    | 'close_after_days'
    | 'auto_close'
    | 'closure_email'
    | 'reopen_on_documents'
  >
> & {
  /** With reminders turning on: whether requests already open get them too. */
  apply_to_open?: boolean
}

export interface ReminderSettingsUpdate {
  claims?: ClaimReminderUpdate
  transfers?: Partial<
    Pick<
      TransferReminderSettings,
      | 'interval_days'
      | 'max_reminders'
      | 'cancel_after_days'
      | 'remind_by_default'
      | 'auto_cancel'
      | 'cancellation_email'
    >
  >
}

export function useReminderSettings() {
  return useQuery<ReminderSettings>({
    queryKey: ['reminder-settings'],
    queryFn: () =>
      api.get('/organisations/reminder-settings/').then((r) => r.data),
  })
}

/** What saving these settings would close: claims the next close run would newly close. */
export interface ReminderSettingsPreview {
  closes_now: number
  /** Most claims one close run closes per tenant. */
  per_run: number
}

/** Ask what saving would do, without saving. */
export function usePreviewReminderSettings() {
  return useMutation<ReminderSettingsPreview, Error, ReminderSettingsUpdate>({
    mutationFn: (data) =>
      api
        .patch('/organisations/reminder-settings/', { ...data, preview: true })
        .then((r) => r.data),
  })
}

export function useUpdateReminderSettings() {
  const qc = useQueryClient()
  return useMutation<ReminderSettings, Error, ReminderSettingsUpdate>({
    mutationFn: (data) =>
      api.patch('/organisations/reminder-settings/', data).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['reminder-settings'] })
    },
  })
}
